@let hasShiftUpdatePermissions =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasServiceUpdatePermissions =
  can(RoleAction.Update, RolePermissionSubject.Service);
@let hasClientReadPermission =
  can(RoleAction.Read, RolePermissionSubject.Client);
@let hasUpdatePermission =
  isServiceActivity() ? hasServiceUpdatePermissions : hasShiftUpdatePermissions;
@let forbiddenMessage = 'general.forbidden_action' | translate;

<form [formGroup]="form">
  <!-- Category -->
  <label class="d-flex align-items-center fw-normal text-dark">
    {{ 'page.shifts.activity' | translate }}
  </label>
  <nz-form-item>
    <nz-form-control
      [nzValidateStatus]="
        !formControls.activityCategory.value ? 'error' : 'success'
      "
    >
      <nz-select
        formControlName="activityCategory"
        [nzPlaceHolder]="'---'"
        [nzMaxTagCount]="1"
        nzShowSearch
        nzAllowClear
        [nzShowArrow]="true"
        [compareWith]="compareActivityFn"
        [nzDisabled]="!hasUpdatePermission"
        nz-tooltip
        [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
        (ngModelChange)="updateFormValue()"
      >
        @for (category of activityCategories; track category) {
          <nz-option
            [nzValue]="category"
            [nzLabel]="category | translateActivityCategory"
          >
          </nz-option>
        }
      </nz-select>
    </nz-form-control>
  </nz-form-item>

  <!-- Name -->
  <label class="d-flex align-items-center fw-normal text-dark">
    {{ 'page.shifts.service_number' | translate }}
  </label>
  <nz-form-item>
    <nz-form-control>
      <input
        nz-input
        [readonly]="!hasUpdatePermission"
        nz-tooltip
        [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
        type="text"
        placeholder="---"
        formControlName="name"
        (focusout)="updateFormValue()"
      />
    </nz-form-control>
  </nz-form-item>
  <!-- //END Name -->

  <!-- Location Fields -->
  @if (
    {
      sector: sector$ | async,
    };
    as data
  ) {
    <label class="d-flex align-items-center fw-normal text-dark">
      {{ 'page.services.from' | translate }}
    </label>
    <nz-form-item>
      <nz-form-control>
        <wilson-location-selects
          [sector]="data.sector?.name"
          [selectedLocation]="formControls.startLocation.value"
          [invalid]="
            !!formControls.startLocation.touched &&
            !formControls.startLocation.value
          "
          [disabled]="!hasUpdatePermission || readonly"
          [readonly]="!hasUpdatePermission || readonly"
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          (locationChange)="setStartLocation($event)"
          (touched)="formControls.startLocation.markAsTouched()"
        ></wilson-location-selects>
      </nz-form-control>
    </nz-form-item>
    @if (displayPreviousLocationMismatchWarning()) {
      <wilson-activity-drawer-location-mismatch
        [isDisabled]="!hasUpdatePermission || readonly"
        nz-tooltip
        [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
        (updateLocation)="updateActivityStartLocation()"
      >
        <span class="mismatch-location">
          {{
            'page.shift_timeline.activity_drawer.location_mismatch_button.start_time.description'
              | translate
          }}
          {{ previousActivityMismatchingEndLocation?.locationCode }} -
          {{ previousActivityMismatchingEndLocation?.name }}
        </span>
      </wilson-activity-drawer-location-mismatch>
    }
    <div class="mb-2">
      <div class="end-location-label">
        <label class="d-flex align-items-center fw-normal text-dark">
          {{ 'page.services.to' | translate }}
        </label>
        <wilson-via-point-button
          [sectorName]="data.sector?.name"
          [disabled]="
            !hasUpdatePermission ||
            readonly ||
            !formControls.startLocation.value ||
            !formControls.endLocation.value
          "
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          [viaPointIds]="form.get('viaPointIds')?.value"
          [startLocation]="formControls.startLocation.value"
          [endLocation]="formControls.endLocation.value"
          (newViaLocationIds)="setViaPointIds($event)"
        ></wilson-via-point-button>
      </div>
      <nz-form-item>
        <nz-form-control>
          <wilson-location-selects
            [sector]="data.sector?.name"
            [selectedLocation]="formControls.endLocation.value"
            [invalid]="
              !!formControls.endLocation.touched &&
              !formControls.endLocation.value
            "
            [disabled]="!hasUpdatePermission || readonly"
            [readonly]="!hasUpdatePermission || readonly"
            nz-tooltip
            [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
            (locationChange)="setEndLocation($event)"
            (touched)="formControls.endLocation.markAsTouched()"
          ></wilson-location-selects>
        </nz-form-control>
      </nz-form-item>
      @if (displayNextLocationMismatchWarning()) {
        <wilson-activity-drawer-location-mismatch
          [isDisabled]="!hasUpdatePermission || readonly"
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          (updateLocation)="updateActivityEndLocation()"
        >
          <span class="mismatch-location">
            {{
              'page.shift_timeline.activity_drawer.location_mismatch_button.end_time.description'
                | translate
            }}
            {{ nextActivityMismatchingStartingLocation?.locationCode }} -
            {{ nextActivityMismatchingStartingLocation?.name }}
          </span>
        </wilson-activity-drawer-location-mismatch>
      }
    </div>
  }
  <!-- //END Location Fields -->

  <!-- Date fields -->
  <div class="d-flex">
    <label class="d-flex align-items-center fw-normal text-dark">
      {{ 'page.shift.activity.planned_start' | translate }}
    </label>
    &nbsp;
    <span> ({{ originalStartDateTime() | localizedDateFormat }})</span>
  </div>

  <nz-form-item>
    <nz-form-control>
      <nz-time-picker
        wilsonTimeAutoFormat
        [nzDisabled]="!hasUpdatePermission"
        [nzStatus]="
          form.touched && formControls.startDateTime.invalid ? 'error' : null
        "
        nz-tooltip
        [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
        formControlName="startDateTime"
      ></nz-time-picker>
    </nz-form-control>
  </nz-form-item>

  <label class="d-flex align-items-center fw-normal text-dark">
    {{ 'page.shift.activity.planned_end' | translate }}
    {{ (isEndDateNextDay$ | async) ? '(+1)' : '' }}
  </label>
  <nz-form-item>
    <nz-form-control>
      <nz-time-picker
        wilsonTimeAutoFormat
        [nzDisabled]="!hasUpdatePermission"
        [nzStatus]="
          form.touched && formControls.endDateTime.invalid ? 'error' : null
        "
        nz-tooltip
        [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
        formControlName="endDateTime"
      ></nz-time-picker>
    </nz-form-control>
  </nz-form-item>
  <!-- //END Date fields -->

  <!-- Agreement field -->
  @if (FeatureName.Billing | featurePure | async) {
    <label class="d-flex align-items-center fw-normal text-dark">
      {{ 'activity.agreement' | translate }}
    </label>
    @if (agreementOptions$ | async; as agreementOptions) {
      @if (
        !isServiceActivity || formControls.agreement.invalid || form.disabled
      ) {
        <div>
          @if (formControls.agreement.value; as agreement) {
            <div>
              {{
                agreement[agreement.length - 1]
                  | selectedAgreementName: agreementOptions
              }}
            </div>
          } @else {
            ---
          }
        </div>
      } @else {
        @if (hasClientReadPermission) {
          <nz-cascader
            class="w-100"
            [nzOptions]="agreementOptions"
            [nzShowSearch]="true"
            formControlName="agreement"
            [nzDisabled]="!hasUpdatePermission"
            nz-tooltip
            [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
            (ngModelChange)="updateFormValue()"
          >
          </nz-cascader>
        } @else {
          <nz-cascader
            class="w-100"
            [nzShowSearch]="true"
            nz-tooltip
            nzDisabled
            [nzTooltipTitle]="forbiddenMessage"
          ></nz-cascader>
        }
      }
    }
  }
  <!-- //END Agreement field -->

  <!-- Profession field -->
  @if ('portal-professions' | featureFlagPure | async) {
    <label class="d-flex align-items-center fw-normal text-dark mt-4">
      {{ 'activity.profession' | translate }}
    </label>
    <nz-form-item class="mb-0">
      <nz-form-control>
        <nz-select
          formControlName="professionId"
          [nzPlaceHolder]="'---'"
          [nzMaxTagCount]="1"
          nzShowSearch
          nzAllowClear
          [nzShowArrow]="true"
          [nzDisabled]="!hasUpdatePermission || form.disabled"
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          (ngModelChange)="updateFormValue()"
        >
          @for (profession of professions$ | async; track profession) {
            <nz-option
              [nzValue]="profession.id"
              [nzLabel]="'profession.' + profession.id | translate"
            >
            </nz-option>
          }
        </nz-select>
      </nz-form-control>
    </nz-form-item>
  }
  <!-- //END Profession field -->

  <!-- Order Number field -->
  @if ('portal-activity-order-number' | featureFlagPure | async) {
    <label class="d-flex align-items-center fw-normal text-dark mt-4">
      {{ 'component.amount_of_wagon.order_number' | translate }}
    </label>
    <nz-form-item class="mb-0">
      <nz-form-control>
        <input
          nz-input
          [readonly]="!hasUpdatePermission"
          formControlName="orderNumber"
          [placeholder]="'---'"
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          (ngModelChange)="updateFormValue()"
          [nzStatus]="formControls.orderNumber.invalid ? 'error' : null"
        />
      </nz-form-control>
    </nz-form-item>
  }
  <!-- //END Profession field -->

  <!-- Wagon Number field -->
  @if ('portal-activity-wagon-number' | featureFlagPure | async) {
    <label class="d-flex align-items-center fw-normal text-dark mt-4">
      {{ 'component.amount_of_wagon.wagon_number' | translate }}
    </label>
    <nz-form-item class="mb-0">
      <nz-form-control>
        <input
          nz-input
          [readonly]="!hasUpdatePermission"
          formControlName="wagonNumber"
          [placeholder]="'---'"
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          (ngModelChange)="updateFormValue()"
          [nzStatus]="formControls.wagonNumber.invalid ? 'error' : null"
        />
      </nz-form-control>
    </nz-form-item>
  }
  <!-- //END Wagon Number field -->

  <!-- Locomotive Number field -->
  @if ('portal-activity-locomotive-number' | featureFlagPure | async) {
    <label class="d-flex align-items-center fw-normal text-dark mt-4">
      {{ 'component.amount_of_wagon.locomotive_number' | translate }}
    </label>
    <nz-form-item class="mb-0">
      <nz-form-control>
        <input
          nz-input
          [readonly]="!hasUpdatePermission"
          formControlName="locomotiveNumber"
          [placeholder]="'---'"
          nz-tooltip
          [nzTooltipTitle]="!hasUpdatePermission ? forbiddenMessage : ''"
          (ngModelChange)="updateFormValue()"
          [nzStatus]="formControls.locomotiveNumber.invalid ? 'error' : null"
        />
      </nz-form-control>
    </nz-form-item>
  }
  <!-- //END Locomotive Number field -->
</form>
